// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL
/* eslint-disable no-template-curly-in-string */

import React, { useState, useEffect, useCallback } from 'react';
import * as Cesium from 'cesium';
import { Paper, Divider, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { classification2Classes, classification6Classes, classification6ClassesNew, defaultDxfPtSize, defaultLazPtSize } from 'Utils/constants';
import { getTileStyleConditions } from 'Utils/map';
import stylesDef from './styles';
import FileErrors from './FileErrors';
import { SetToggleClassAction } from '../viewer3DActions';
import PointSizeTool from './PointSizeTool';

Cesium.Ion.defaultAccessToken = _CESIUM_KEY_;
const terrain = Cesium.Terrain.fromWorldTerrain();
const mapboxImagery = new Cesium.ImageryLayer(new Cesium.MapboxImageryProvider({
  mapId: 'mapbox.satellite',
  accessToken: _MAPBOX_KEY_,
}));

interface Map3DProps {
  lasFilesWith3DTile: ILasFile[];
  showPointCloud: boolean;
  showTerrain: boolean;
  showClassifiedPointCloud: {[key: string]: boolean};
  resetZoom: boolean;
  setResetZoom: (show: boolean) => void;
  lasFilesDisplayStatuses: any;
}

const Map3D = ({ lasFilesWith3DTile, showPointCloud, showClassifiedPointCloud, showTerrain, resetZoom, lasFilesDisplayStatuses, setResetZoom }: Map3DProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const [viewer, setViewer] = useState(null);
  const [tilesets, setTilesets] = useState(null);
  const [ppsm, setPpsm] = useState(null);
  const [layerDataSource] = useState<{ [key: string]: any }>({});
  const [anchor, setAnchor] = useState(null);
  const [inputPtSize, setInputPtSize] = useState(defaultLazPtSize);
  const [outputPtSize, setOutputPtSize] = useState(defaultLazPtSize);
  const [dxfPtSize, setDxfPtSize] = useState(defaultDxfPtSize);
  const [has3dPoint, setHas3dPoint] = useState(false);
  const [hasOutputPt, setHasOutputPt] = useState(false);
  const { linework3D, layer3DChange, cad3dLayers } = useAppSelector(
    (state) => state.viewer3D,
  );
  const toggleClass = useAppSelector((state) => state.viewer3D.toggleClass);
  const classVisible = useAppSelector((state) => state.viewer3D.classVisible);

  useEffect(() => {
    tilesets?.forEach((tileset: any) => {
      if (tileset?.orderId) {
        tileset.show = showClassifiedPointCloud[tileset.orderId];
      } else if (tileset) {
        const tilesetFileName = tileset.basePath.split('/').slice(-2, -1)[0].replace(/_3d_tile$/, '');
        tileset.show = showPointCloud && lasFilesDisplayStatuses[tilesetFileName];
      }
    });
  }, [showPointCloud, showClassifiedPointCloud, lasFilesDisplayStatuses]);

  useEffect(() => {
    for (let i = 0; i < layer3DChange.length; i += 2) {
      const name = layer3DChange[i];
      if (layerDataSource[name]) {
        layerDataSource[name].show = layer3DChange[i + 1];
      }
    }
  }, [layer3DChange]);

  useEffect(() => {
    if (toggleClass && tilesets?.length) {
      const tileset = tilesets.find((tile: any) => tile?.orderId === toggleClass);
      if (!tileset) return;
      const lasVisible = classVisible[toggleClass];
      let classArray: any = [];
      if (tileset.newClasses) {
        classArray = classification6ClassesNew;
      } else if (tileset.classNumber === 2) {
        classArray = classification2Classes;
      } else if (tileset.classNumber === 6) {
        classArray = classification6Classes;
      }

      const showConditions = Object.keys(lasVisible).map((layer) => {
        const classInfo = classArray.find((item: any) => item.name === layer);
        return [`\${Classification}===${classInfo.classId}`, lasVisible[layer] ? 'true' : 'false'];
      });

      let tileConditions;
      if (tileset.newClasses) {
        tileConditions = getTileStyleConditions(classification6ClassesNew);
      } else if (tileset.classNumber === 2) {
        tileConditions = getTileStyleConditions(classification2Classes);
      } else if (tileset.classNumber === 6) {
        tileConditions = getTileStyleConditions(classification6Classes);
      }

      const newStyle = new Cesium.Cesium3DTileStyle({
        color: {
          conditions: tileConditions,
        },
        show: {
          conditions: showConditions,
        },
        pointSize: outputPtSize,
      });

      tileset.style = newStyle;
      dispatch(SetToggleClassAction(null));
    }
  }, [toggleClass]);

  useEffect(() => {
    if (viewer) {
      if (showTerrain) {
        viewer.scene.globe.show = true;
        viewer.terrain = terrain;
      } else {
        viewer.scene.globe.show = false;
      }
    }
  }, [showTerrain]);

  useEffect(() => {
    if (viewer && tilesets?.length && resetZoom) {
      try {
        zoomToCenter(tilesets);
      } catch (error) {
        // zoom to failed
      }
      setResetZoom(false);
    }
  }, [resetZoom]);

  useEffect(() => {
    if (viewer) {
      linework3D.forEach((site) => {
        const { site_id, data } = site;
        data.forEach((layer) => {
          const layerName = `${site_id}-${layer.layer_id}`;
          if (!layerDataSource[layerName]) {
            const { geomtype } = layer;
            if (geomtype === 'point') {
              const pointCollection = new Cesium.PointPrimitiveCollection();
              viewer.scene.primitives.add(pointCollection);
              layer.offset.forEach((point) => {
                if (point.length % 3 === 0) {
                  pointCollection.add({
                    position: Cesium.Cartesian3.fromDegrees(point[0], point[1], point[2]),
                    color: Cesium.Color.fromCssColorString(cad3dLayers[site_id]?.layers?.find((l) => l.layer_id === layer.layer_id)?.color || '#ffffff'),
                    pixelSize: dxfPtSize,
                  });
                }
              });
              layerDataSource[layerName] = pointCollection;
              setHas3dPoint(true);
            } else if (geomtype === 'line') {
              const lineCollection = new Cesium.PolylineCollection();
              viewer.scene.primitives.add(lineCollection);
              layer.offset.forEach((line) => {
                if (line.length % 3 === 0) {
                  lineCollection.add({
                    positions: Cesium.Cartesian3.fromDegreesArrayHeights(line),
                    width: 2,
                    material: Cesium.Material.fromType('Color', {
                      color: Cesium.Color.fromCssColorString(cad3dLayers[site_id]?.layers?.find((l) => l.layer_id === layer.layer_id)?.color || '#ffffff'),
                    }),
                  });
                }
              });
              layerDataSource[layerName] = lineCollection;
            }
          }
        });
      });
    }
  }, [linework3D, viewer]);

  useEffect(() => {
    const load3dTile = async () => {
      const outputLasExist = lasFilesWith3DTile.some((las) => las.ownerOrder);
      setHasOutputPt(outputLasExist);
      let lasTilesets = await Promise.all(lasFilesWith3DTile.map(async (lasFile) => {
        try {
          // eslint-disable-next-line no-restricted-globals
          const url = isNaN(Number(lasFile.tile3DUrl)) ? lasFile.tile3DUrl : await Cesium.IonResource.fromAssetId(+lasFile.tile3DUrl);
          const newTileset = await Cesium.Cesium3DTileset.fromUrl(url);
          if (lasFile.ownerOrder && lasFile.newClasses) {
            newTileset.orderId = lasFile.ownerOrder;
            newTileset.classNumber = lasFile.classes;
            newTileset.newClasses = true;
            newTileset.style = new Cesium.Cesium3DTileStyle({
              color: {
                conditions: getTileStyleConditions(classification6ClassesNew),
              },
              pointSize: outputPtSize,
            });
          } else if (lasFile.ownerOrder && lasFile.classes === 6) {
            newTileset.orderId = lasFile.ownerOrder;
            newTileset.classNumber = 6;
            newTileset.style = new Cesium.Cesium3DTileStyle({
              color: {
                conditions: getTileStyleConditions(classification6Classes),
              },
              pointSize: outputPtSize,
            });
          } else if (lasFile.ownerOrder && lasFile.classes === 2) {
            newTileset.orderId = lasFile.ownerOrder;
            newTileset.classNumber = 2;
            newTileset.style = new Cesium.Cesium3DTileStyle({
              color: {
                conditions: getTileStyleConditions(classification2Classes),
              },
              pointSize: outputPtSize,
            });
          } else if (outputLasExist && !lasFile.ownerOrder) {
            newTileset.show = false;
          }
          viewer.scene.primitives.add(newTileset);
          return newTileset;
        } catch (error) {
          return undefined;
        }
      }));

      lasTilesets = lasTilesets.filter((tileset) => tileset);

      if (lasTilesets.length) {
        setTilesets(lasTilesets);

        zoomToCenter(lasTilesets);
      }
    };

    if (!tilesets && viewer) {
      const { totalPoints, totalAcreage } = lasFilesWith3DTile.filter((lasFile) => !lasFile.ownerOrder && lasFile.acreage > 0).reduce((acc, curr) => {
        const acreageInSquareMeters = curr.acreage * 4046.85642;
        return {
          totalPoints: acc.totalPoints + curr.points,
          totalAcreage: acc.totalAcreage + acreageInSquareMeters,
        };
      }, { totalPoints: 0, totalAcreage: 0 });
      const pointsPerSquareMeter = totalPoints / totalAcreage;
      setPpsm(pointsPerSquareMeter.toFixed(3));

      load3dTile();
    }
  }, [lasFilesWith3DTile, viewer]);

  useEffect(() => {
    tilesets?.forEach((tileset: any) => {
      if (!tileset?.orderId) {
        tileset.style = new Cesium.Cesium3DTileStyle({
          pointSize: inputPtSize,
        });
      }
    });
  }, [inputPtSize]);

  useEffect(() => {
    tilesets?.forEach((tileset: any) => {
      if (tileset?.orderId) {
        let classArray: any = [];
        if (tileset.newClasses) {
          classArray = classification6ClassesNew;
        } else if (tileset.classNumber === 2) {
          classArray = classification2Classes;
        } else if (tileset.classNumber === 6) {
          classArray = classification6Classes;
        }
        const lasVisible = classVisible[tileset.orderId];
        const showConditions = Object.keys(lasVisible).map((layer) => {
          const classInfo = classArray.find((item: any) => item.name === layer);
          return [`\${Classification}===${classInfo.classId}`, lasVisible[layer] ? 'true' : 'false'];
        });

        const conditions = getTileStyleConditions(classArray);

        tileset.style = new Cesium.Cesium3DTileStyle({
          color: {
            conditions,
          },
          show: {
            conditions: showConditions,
          },
          pointSize: outputPtSize,
        });
      }
    });
  }, [outputPtSize]);

  useEffect(() => {
    Object.keys(layerDataSource)?.forEach((key) => {
      const layerSource = layerDataSource[key];
      if (layerSource?._pointPrimitives?.length) {
        const needsUpdate = layerSource._pointPrimitives[0].pixelSize !== dxfPtSize;
        if (needsUpdate) {
          layerSource._pointPrimitives.forEach((point) => {
            point.pixelSize = dxfPtSize;
          });
        }
      }
    });
  }, [dxfPtSize]);

  const zoomToCenter = (lasTilesets: any) => {
    if (!lasTilesets || lasTilesets.length === 0 || !viewer) {
      return;
    }
    const boundingSpheres = lasTilesets.map((tileset: any) => tileset.boundingSphere);
    const combinedBoundingSphere = Cesium.BoundingSphere.fromBoundingSpheres(boundingSpheres);

    // Zoom to the combined bounding sphere
    viewer.camera.flyToBoundingSphere(combinedBoundingSphere, {
      offset: new Cesium.HeadingPitchRange(0.0, -0.5, combinedBoundingSphere.radius * 2.0),
      duration: 1,
    });
  };

  const mapRef = useCallback((node) => {
    if (node !== null) {
      const newViewer = new Cesium.Viewer(node, {
        terrain,
        animation: false,
        baseLayerPicker: false,
        fullscreenButton: false,
        geocoder: false,
        homeButton: false,
        infoBox: false,
        sceneModePicker: false,
        selectionIndicator: false,
        timeline: false,
        navigationInstructionsInitiallyVisible: false,
        baseLayer: mapboxImagery,
        creditContainer: 'creditContainer',
      });
      newViewer.scene.globe.show = false;
      newViewer.scene.skyAtmosphere.destroy();
      newViewer.scene.skyBox.destroy();
      newViewer.scene.sun.destroy();
      newViewer.scene.globe.baseColor = Cesium.Color.BLACK;
      newViewer.scene.screenSpaceCameraController.minimumZoomDistance = 5;
      setViewer(newViewer);
    }
  }, [terrain]);

  const zoomIn = () => {
    viewer.camera.zoomIn(30);
  };

  const zoomOut = () => {
    viewer.camera.zoomOut(30);
  };

  const handleAdjustPtSizeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor(anchor ? null : event.currentTarget);
  };

  return (
    <>
      <FileErrors />
      <Box sx={styles.infoBar}>
        <Button sx={styles.adjustButton} onClick={handleAdjustPtSizeClick}>Adjust Pt Cloud</Button>
        <PointSizeTool
          anchor={anchor}
          inputPtSize={inputPtSize}
          setInputPtSize={setInputPtSize}
          outputPtSize={outputPtSize}
          setOutputPtSize={setOutputPtSize}
          dxfPtSize={dxfPtSize}
          setDxfPtSize={setDxfPtSize}
          has3dPoint={has3dPoint}
          hasOutputPt={hasOutputPt}
        />
        {ppsm > 0 && (
          <Typography sx={styles.pointsText} variant="body1">
            {`${ppsm} pts/m`}
            <sup>2</sup>
          </Typography>
        )}
        <Box sx={styles.creditContainer} key="creditContainer" id="creditContainer" />
      </Box>
      <Box sx={styles.map} ref={mapRef} />
      <Paper elevation={1} square sx={styles.mapControls}>
        <Button sx={styles.mapButton} onClick={zoomIn}>
          <AddIcon />
        </Button>
        <Divider sx={styles.divider} />
        <Button sx={styles.mapButton} onClick={zoomOut}>
          <RemoveIcon />
        </Button>
      </Paper>
    </>
  );
};

export default Map3D;
